// Shared by the admin console, website and mini program. A visit is an
// application entry, not an authentication/refresh session.
export function createVisitRecorder({ getToken, enter, leave, retainOnHide = false }) {
  let visible = false;
  let current = null;

  function close(visit, token) {
    if (!visit || visit.closed || !visit.id) return Promise.resolve();
    visit.closed = true;
    return Promise.resolve(leave(visit.id, token || visit.token)).catch(() => {});
  }

  function ensure() {
    const token = getToken();
    if (!visible || !token || current) return Promise.resolve();
    const visit = { token, id: 0, ended: false, closed: false };
    current = visit;
    visit.ready = Promise.resolve(enter(token))
      .then((result) => {
        visit.id = Number(result.id) || 0;
        if (visit.ended) return close(visit, visit.leaveToken);
      })
      .catch(() => {
        if (current === visit) current = null;
      });
    return visit.ready;
  }

  function end() {
    const visit = current;
    current = null;
    if (!visit) return Promise.resolve();
    visit.ended = true;
    visit.leaveToken = getToken() || visit.token;
    return visit.id ? close(visit, visit.leaveToken) : visit.ready || Promise.resolve();
  }

  return {
    show() {
      visible = true;
      return ensure();
    },
    ensure,
    hide() {
      visible = false;
      // Mini-program onHide also covers authorization and temporary switches
      // to WeChat. Keep this runtime's visit until explicit logout/re-login.
      if (retainOnHide) return Promise.resolve();
      return end();
    },
    // Explicit logout ends the current visit while the credentials still
    // exist. A subsequent login can start another visit on the same page.
    end,
    authenticated(id, token) {
      if (!id) return;
      const previous = current;
      current = null;
      if (previous) {
        previous.ended = true;
        previous.leaveToken = previous.token;
        if (previous.id) void close(previous, previous.token);
      }
      const visit = { token, id: Number(id), ended: !visible && !retainOnHide, closed: false };
      if (visible || retainOnHide) current = visit;
      else void close(visit, token);
    },
  };
}

export function createBrowserVisitRecorder({ getToken, baseUrl }) {
  function request(path, data, token, keepalive) {
    return fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
      keepalive,
    })
      .then((response) => response.json())
      .then((body) => {
        if (body.code !== 1) throw new Error('visit record unavailable');
        return body.result;
      });
  }
  return createVisitRecorder({
    getToken,
    enter: (token) => request('visit_enter', {}, token, false),
    leave: (id, token) => request('visit_leave', { id }, token, true),
  });
}

export function bindBrowserVisits(recorder, page = window, doc = document) {
  const show = () => {
    if (doc.visibilityState !== 'hidden') void recorder.show();
  };
  // Switching tabs or minimizing the browser is not leaving the application.
  // Only pagehide (navigation/close) or explicit logout closes this visit.
  // A visibility event can still start an initially hidden page when shown.
  doc.addEventListener('visibilitychange', show);
  page.addEventListener('pagehide', () => {
    void recorder.hide();
  });
  page.addEventListener('pageshow', show);
  show();
}
